/**
 * Produits vus récemment.
 *
 * Le handle de la fiche courante est empilé dans le localStorage, puis les
 * fiches précédentes sont récupérées via /products/{handle}.js. Si le stockage
 * est indisponible (navigation privée stricte), la section reste masquée.
 */
(() => {
  const KEY = 'ag-recently-viewed';
  const MEMORY = 12;

  const read = () => {
    try {
      const raw = localStorage.getItem(KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  };

  const remember = (handle) => {
    if (!handle) return;
    try {
      const list = [handle, ...read().filter((item) => item !== handle)].slice(0, MEMORY);
      localStorage.setItem(KEY, JSON.stringify(list));
    } catch {
      /* stockage indisponible : on n'enregistre rien */
    }
  };

  const render = async (root) => {
    const current = root.dataset.currentHandle;
    const limit = parseInt(root.dataset.limit, 10) || 4;
    const handles = read()
      .filter((handle) => handle !== current)
      .slice(0, limit);
    if (!handles.length) return;

    const products = (
      await Promise.all(
        handles.map((handle) =>
          fetch(`/products/${encodeURIComponent(handle)}.js`)
            .then((response) => (response.ok ? response.json() : null))
            .catch(() => null)
        )
      )
    ).filter(Boolean);
    if (!products.length) return;

    const money = new Intl.NumberFormat(document.documentElement.lang || 'fr', {
      style: 'currency',
      currency: root.dataset.currency || 'EUR',
    });

    const grid = root.querySelector('[data-ag-recently-grid]');
    for (const product of products) {
      const card = document.createElement('article');
      card.className = 'ag-recently__card';

      const media = document.createElement('a');
      media.className = 'ag-recently__media';
      media.href = product.url;
      if (product.featured_image) {
        const image = document.createElement('img');
        image.src = product.featured_image.replace(/(\.[a-z]+)(\?|$)/i, '_600x$1$2');
        image.alt = product.title;
        image.loading = 'lazy';
        media.appendChild(image);
      }
      card.appendChild(media);

      const meta = document.createElement('div');
      meta.className = 'ag-product__meta';

      const title = document.createElement('a');
      title.className = 'ag-product__title';
      title.href = product.url;
      title.textContent = product.title;
      meta.appendChild(title);

      // Même repère visuel que les autres cartes. Cette section étant rendue
      // côté client, le « + » renvoie à la fiche plutôt que de reconstruire
      // à la main le formulaire d'ajout au panier du thème.
      const add = document.createElement('a');
      add.className = 'ag-card__add';
      add.href = product.url;
      add.setAttribute('aria-label', `Voir ${product.title}`);
      add.innerHTML =
        '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">' +
        '<path d="M8 2v12M2 8h12" stroke="currentColor" stroke-width="1.2"/></svg>';
      meta.appendChild(add);

      card.appendChild(meta);

      const price = document.createElement('span');
      price.className = 'ag-product__price';
      price.textContent = money.format(product.price / 100);
      card.appendChild(price);

      grid.appendChild(card);
    }

    root.hidden = false;
  };

  const init = () => {
    const root = document.querySelector('[data-ag-recently]');
    if (!root) return;
    render(root).finally(() => remember(root.dataset.currentHandle));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
